import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { supportedLanguages, SupportedLanguage } from '@/i18n';
import { useOrgSettings } from '@/hooks/useOrgSettings';

export function useLanguage() {
  const { i18n } = useTranslation();
  const { profile, user, refreshProfile, currentOrgId } = useAuth();
  const { orgSettings, isLoading } = useOrgSettings();
  const orgLanguage = (orgSettings?.default_language as SupportedLanguage) || 'sv';

  // Apply language resolution: user pref → org default → 'en'
  useEffect(() => {
    if (isLoading) return;

    const userLang = (profile as any)?.preferred_language as SupportedLanguage | null;
    const resolvedLang = userLang || orgLanguage || 'en';
    
    if (i18n.language !== resolvedLang) {
      i18n.changeLanguage(resolvedLang);
    }
  }, [(profile as any)?.preferred_language, orgLanguage, isLoading, i18n]);

  // Function to change language and persist
  const setLanguage = useCallback(async (lang: SupportedLanguage | null) => {
    // null means "use org default"
    const effectiveLang = lang || orgLanguage || 'en';
    
    // Change language immediately for instant feedback
    i18n.changeLanguage(effectiveLang);

    if (user) {
      await supabase
        .from('profiles')
        .update({ preferred_language: lang })
        .eq('id', user.id);
      // Refresh profile to update the context state
      await refreshProfile();
    } else {
      // For non-logged in users, store in localStorage
      if (lang) {
        localStorage.setItem('preferred_language', lang);
      } else {
        localStorage.removeItem('preferred_language');
      }
    }
  }, [user, orgLanguage, i18n, refreshProfile]);

  // Get the display name for a language code
  const getLanguageName = useCallback((code: string, native = false) => {
    const lang = supportedLanguages.find(l => l.code === code);
    return native ? lang?.nativeLabel || code : lang?.label || code;
  }, []);

  return {
    language: i18n.language as SupportedLanguage,
    userPreference: (profile as any)?.preferred_language as SupportedLanguage | null,
    orgDefaultLanguage: orgLanguage,
    setLanguage,
    getLanguageName,
    isLoading,
    availableLanguages: supportedLanguages,
  };
}
